import { useLocale } from '@planetra/i18n';
import { useEffect, useMemo, type ReactNode } from 'react';
import type { PortalClient } from './client';
import { ApiClientContext } from './context';
import { portalClient, setLocaleSource } from './portal-client';

export interface ApiProviderProps {
  readonly children: ReactNode;
  /** Tests inject a fake. The app leaves it out and gets the singleton. */
  readonly client?: PortalClient;
}

/**
 * Supplies the API client to components, and keeps `Accept-Language` in step
 * with the locale switcher (P0-WEB-04).
 *
 * The locale is pushed into the client rather than read from it, because the
 * client is also used by loaders that have no access to React state. One
 * subscription here keeps both paths sending the same header.
 */
export function ApiProvider({ children, client }: ApiProviderProps) {
  // Resolved lazily: `portalClient()` reads the validated config, and building
  // it during module evaluation would throw in a test that never makes a call.
  const resolved = useMemo(() => client ?? portalClient(), [client]);
  const { locale } = useLocale();

  useEffect(() => {
    setLocaleSource(() => locale);
  }, [locale]);

  return <ApiClientContext value={resolved}>{children}</ApiClientContext>;
}
